import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  getDisplayCurrency,
  getDisplaySymbol,
  getCurrentRate,
  isConversionEnabled,
  formatMoney,
  convertFromUSD,
  subscribeToCurrencyChanges,
  CURRENCY_SYMBOLS,
} from '../utils/currency-utils';

/**
 * Hook to get and react to global display currency changes
 * Components using this hook will re-render when the currency changes
 * 
 * @example
 * const { formatAmount, displaySymbol, refreshKey } = useDisplayCurrency();
 * 
 * // Use refreshKey in AG Grid key to force refresh
 * <AgGridReact key={`grid-${refreshKey}`} ... />
 * 
 * // Format amounts
 * const formatted = formatAmount(usdValue);
 */
const getStoredDecimalPrecision = () => {
  try {
    const stored = localStorage.getItem('global_decimal_precision');
    return stored ? parseInt(stored, 10) : 3;
  } catch {
    return 3;
  }
};

export const useDisplayCurrency = () => {
  // State that tracks currency configuration
  const [currencyState, setCurrencyState] = useState(() => ({
    currency: getDisplayCurrency(),
    symbol: getDisplaySymbol(),
    rate: getCurrentRate(),
  }));

  const [decimalPrecision, setDecimalPrecision] = useState(getStoredDecimalPrecision);

  // Refresh key that increments on currency change - use this to force AG Grid refresh
  const [refreshKey, setRefreshKey] = useState(0);

  // Subscribe to currency changes
  useEffect(() => {
    const handleChange = () => {
      setCurrencyState({
        currency: getDisplayCurrency(),
        symbol: getDisplaySymbol(),
        rate: getCurrentRate(),
      });
      setRefreshKey(prev => prev + 1);
    };

    return subscribeToCurrencyChanges(handleChange);
  }, []);

  // Subscribe to decimal precision changes
  useEffect(() => {
    const handleDecimalChange = (e) => {
      const newVal = e.detail?.decimals;
      if (newVal !== undefined) {
        setDecimalPrecision(newVal);
        setRefreshKey(prev => prev + 1);
      }
    };
    window.addEventListener('globalDecimalChanged', handleDecimalChange);
    return () => window.removeEventListener('globalDecimalChanged', handleDecimalChange);
  }, []);

  // Format USD amount in display currency
  const formatAmount = useCallback((usdAmount, decimals) => {
    return formatMoney(usdAmount, { decimals: decimals ?? decimalPrecision });
  }, [decimalPrecision]);

  // Format with symbol
  const formatAmountWithSymbol = useCallback((usdAmount, decimals) => {
    return formatMoney(usdAmount, { decimals: decimals ?? decimalPrecision, showSymbol: true });
  }, [decimalPrecision]);

  // Convert USD to display currency (returns number)
  const convertAmount = useCallback((usdAmount) => {
    return convertFromUSD(usdAmount);
  }, []);

  // Get rate for a specific currency
  const getRate = useCallback((currencyCode) => {
    const code = (currencyCode || 'USD').toUpperCase();
    if (code === 'USD') return 1;
    return currencyState.rate;
  }, [currencyState.rate]);

  // Derived values
  const conversionEnabled = useMemo(() => isConversionEnabled(), [currencyState]);

  return {
    // Current state
    currency: currencyState.currency,
    displayCurrency: currencyState.currency, // alias
    symbol: currencyState.symbol,
    displaySymbol: currencyState.symbol, // alias
    rate: currencyState.rate,
    exchangeRate: currencyState.rate, // alias for rate
    decimalPrecision,
    
    // Refresh trigger for AG Grid
    refreshKey,
    
    // Functions
    formatAmount,
    formatAmountWithSymbol,
    convertAmount,
    getRate,
    
    // Flags
    isConversionEnabled: conversionEnabled,
    isConverted: conversionEnabled, // alias - true when displaying non-USD currency
    
    // Constants
    currencySymbols: CURRENCY_SYMBOLS,
  };
};

export default useDisplayCurrency;
